<template>
  <div class="max-w-7xl mx-auto py-12 md:py-16 px-4">
    <div v-reveal class="text-center mb-10 md:mb-12">
      <h1 class="font-serif text-4xl md:text-5xl font-bold text-gray-900 leading-tight">All Artists <span class="text-gray-300">/</span> <span class="text-brand-red text-3xl md:text-4xl">সকলো শিল্পী</span></h1>
      <div class="mt-5 w-16 h-1 rounded-full bg-brand-red mx-auto"></div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center gap-3 py-12 text-gray-500">
      <div class="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-brand-red"></div>
      <p class="text-lg">Loading artists...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-10">
      <p class="text-brand-red-dark">{{ error }}</p>
    </div>

    <!-- Two sections: Renowned (published by the Dapon team) and Artist Profiles (self-published) -->
    <template v-else>
      <section v-for="section in sections" :key="section.title" v-show="section.list.length" class="mb-14 last:mb-0">
        <div v-reveal class="section-heading mb-6">
          <h2 class="section-title text-2xl">{{ section.title }}</h2>
          <span class="section-subtitle">{{ section.subtitle }}</span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div
            v-for="(artist, i) in section.list"
            :key="artist.id"
            v-reveal="{ delay: (i % 4) * 0.08 }"
            class="card overflow-hidden group transition-shadow duration-300 hover:shadow-card-hover"
          >
            <div class="relative h-56 overflow-hidden bg-gray-100">
                <img
                :src="artist.image"
                :alt="artist.name"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                @error="handleImageError"
                />
                <span v-if="artist.is_renowned" class="badge absolute left-3 top-3 bg-white/90 text-brand-red-dark shadow-sm">
                  <i class="fa-solid fa-star text-[10px] text-amber-500"></i> Renowned
                </span>
            </div>

            <div class="p-5">
              <h2 class="font-serif text-xl font-bold text-gray-900 mb-0.5 truncate">{{ artist.name }}</h2>
              <p class="text-sm text-brand-red font-medium mb-4 truncate">{{ artist.profession }}</p>

              <router-link
                :to="`/profile/${artist.id}`"
                class="btn w-full bg-gray-100 text-gray-800 hover:bg-brand-red hover:text-white"
              >
                View Profile
              </router-link>
            </div>
          </div>
        </div>
      </section>
    </template>
    <!-- Empty State -->
    <div v-if="!loading && !error && artists.length === 0" class="text-center py-16 card">
        <p class="text-gray-500 text-lg">No artists found.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || "https://sanskritirdapoon.in/api";
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL || "https://sanskritirdapoon.in";

const artists = ref([]);

const sections = computed(() => [
  { title: 'Renowned Artists', subtitle: '( স্বনামধন‍্য শিল্পী )', list: artists.value.filter(a => a.is_renowned) },
  { title: 'Artist Profiles', subtitle: '( শিল্পীসকলক জানো আহক )', list: artists.value.filter(a => !a.is_renowned) },
]);
const loading = ref(true);
const error = ref('');

const resolveImage = (img) => {
  if (!img) return `${FILE_BASE_URL}/images/default.png`;
  if (img.startsWith('data:')) return img;
  if (img.startsWith('http')) return img;
  return `${FILE_BASE_URL}${img}`;
};

const handleImageError = (e) => {
  e.target.src = `${FILE_BASE_URL}/images/default.png`;
};

const fetchArtists = async () => {
    try {
        const res = await fetch(`${API_BASE_URL}/artists`);
        if (!res.ok) throw new Error('Failed to fetch artists');
        const data = await res.json();
        
        // Filter approved artists and map images
        artists.value = data
            .filter(a => a.is_approved)
            .map(a => ({
                ...a,
                image: resolveImage(a.image)
            }));
            
    } catch (err) {
        console.error('Error fetching artists:', err);
        error.value = 'Failed to load artists. Please try again later.';
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchArtists();
    window.scrollTo(0, 0);
});
</script>
